import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import resultsService from '../../services/resultsService';
import scheduleService from '../../services/scheduleService';
import participantsService from '../../services/participantsService';
import { categoriesService, ageGroupsService, groupsService } from '../../services';
import cacheService from '../../utils/cacheService';
import { getAgeGroupColors } from "../../utils/ageGroupColors";
import resultsStreamService from '../../services/resultsStreamService';

const ResultsTailwind = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Prüfe ob es sich um die öffentliche Ansicht handelt
  const isPublicView = location.pathname === "/results";
  
  // Redirect Scorekeeper zur ScorekeeperView
  useEffect(() => {
    if (user && user.role && user.role.toLowerCase() === 'scorekeeper') {
      navigate('/scorekeeper', { replace: true });
    }
  }, [user, navigate]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [savingEventId, setSavingEventId] = useState(null);
  const [resultsData, setResultsData] = useState([]);
  const [allResultsData, setAllResultsData] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [expandedJudgeScores, setExpandedJudgeScores] = useState(new Set());
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [expandedNations, setExpandedNations] = useState(new Set()); // Erweiterte Nationen in Team-Events
  const [editedData, setEditedData] = useState(new Map());
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });
  const [teamData, setTeamData] = useState(null); // Team-Daten für Team-Events
  const [loading, setLoading] = useState(true);
  const [updatedValues, setUpdatedValues] = useState(new Set()); // Track welche Werte sich geändert haben
  const [lastUpdateTimes, setLastUpdateTimes] = useState(new Map()); // Track letzte Aktualisierungszeit pro Gruppe (Map von groupId -> Date)
  const previousPositionsRef = useRef(new Map()); // Track vorherige Positionen für Animationen (Map von eventId -> Map von participantId -> position)
  const activeGroupConnectionsRef = useRef(new Map()); // Track aktive SSE-Verbindungen (Map von groupId -> cleanup function)


  // Lade Daten beim ersten Laden
  useEffect(() => {
    loadCategoriesAndAgeGroups();
    loadAllData();
  }, []);

  // Lese Filter aus URL-Parameter und setze Filter
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    const ageGroupFromUrl = searchParams.get('ageGroup');
    const genderFromUrl = searchParams.get('gender');
    const locationFromUrl = searchParams.get('location');
    
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
    if (ageGroupFromUrl) {
      setSelectedAgeGroup(ageGroupFromUrl);
    }
    if (genderFromUrl) {
      setSelectedGender(genderFromUrl);
    }
    if (locationFromUrl) {
      setSelectedLocation(locationFromUrl);
    }
  }, [searchParams]);

  // Lade Kategorien und Altersgruppen (mit Cache)
  const loadCategoriesAndAgeGroups = async () => {
    try {
      // Prüfe Cache zuerst
      const cachedCategories = cacheService.get('categories');
      const cachedAgeGroups = cacheService.get('ageGroups');

      if (cachedCategories && cachedAgeGroups) {
        setCategories(cachedCategories);
        const validAgeGroups = cachedAgeGroups.filter(ag => ag.name !== 'Alle');
        setAgeGroups(validAgeGroups);
        return; // Verwende Cache, kein Request nötig
      }

      // Cache nicht vorhanden oder abgelaufen, lade vom Backend
      const [categoriesResult, ageGroupsResult] = await Promise.all([
        categoriesService.getAllCategories(),
        ageGroupsService.getAllAgeGroups()
      ]);

      if (categoriesResult.success) {
        const categoriesData = categoriesResult.data || [];
        setCategories(categoriesData);
        cacheService.set('categories', categoriesData);
      } else {
        console.error('Fehler beim Laden der Kategorien:', categoriesResult.error);
        setCategories([]);
      }

      if (ageGroupsResult.success) {
        const allAgeGroups = ageGroupsResult.data || [];
        const validAgeGroups = allAgeGroups.filter(ag => ag.name !== 'Alle');
        setAgeGroups(validAgeGroups);
        cacheService.set('ageGroups', allAgeGroups);
      } else {
        console.error('Fehler beim Laden der Altersgruppen:', ageGroupsResult.error);
        setAgeGroups([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien und Altersgruppen:', error);
      setCategories([]);
      setAgeGroups([]);
    }
  };

  // Lade alle notwendigen Daten
  const loadAllData = async () => {
    try {
      setLoading(true);
      
      // Lade Schedule-Daten (für Events mit Status "Ausstehend")
      try {
        const scheduleResult = await scheduleService.getAllScheduleEvents();
        if (scheduleResult.success && scheduleResult.data) {
          setScheduleData(scheduleResult.data || []);
        } else {
          setScheduleData([]);
        }
      } catch (error) {
        console.warn('Backend-Fehler beim Laden der Schedule-Daten:', error.message);
        setScheduleData([]);
      }

      // Lade Teilnehmer-Daten
      try {
        const participantsResult = await participantsService.getAllParticipants();
        if (participantsResult.success && participantsResult.data) {
          setParticipants(participantsResult.data || []);
        } else {
          setParticipants([]);
        }
      } catch (error) {
        console.warn('Backend-Fehler beim Laden der Teilnehmer-Daten:', error.message);
        setParticipants([]);
      }

      // Lade alle Ergebnisse ohne Filter (nur einmal beim initialen Laden)
      // loadResultsData() wird separat im useEffect mit Filter-Dependencies aufgerufen
      await loadAllResultsData();
      
      // Prüfe ob Team-Events vorhanden sind und lade Team-Daten
      const hasTeamEvent = scheduleData.some(event => 
        event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb'
      );
      
      if (hasTeamEvent) {
        try {
          const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
          if (teamGroupResponse.success && teamGroupResponse.data) {
            setTeamData(teamGroupResponse.data);
          }
        } catch (error) {
          console.debug('Fehler beim Laden der Team-Daten (nicht kritisch):', error);
        }
      }
    } catch (error) {
      console.error('Unerwarteter Fehler beim Laden der Daten:', error);
      // Setze leere Arrays statt Fallback-Daten
      setScheduleData([]);
      setParticipants([]);
      setResultsData([]);
    } finally {
      setLoading(false);
    }
  };

  // Lade Ergebnisse von der Backend-API
  const loadResultsData = async () => {
    try {
      const filters = {};
      if (selectedCategory !== "all") filters.category = selectedCategory;
      if (selectedAgeGroup !== "all") filters.ageGroup = selectedAgeGroup;
      if (selectedGender !== "all") filters.gender = selectedGender;
      if (selectedLocation !== "all") filters.venue = selectedLocation;

      const result = await resultsService.getResultsGroupedByEvents(filters);
      
      if (result.success && result.data) {
        setResultsData(result.data || []);
      } else {
        setResultsData([]);
      }
    } catch (error) {
      console.warn('Backend-Fehler beim Laden der Ergebnisse:', error.message);
      setResultsData([]);
    }
  };

  // Lade alle Ergebnisse ohne Filter (für zuverlässige Anzeige abgeschlossener Gruppen)
  const loadAllResultsData = async () => {
    try {
      const result = await resultsService.getResultsGroupedByEvents({});
      if (result.success) {
        setAllResultsData(result.data || []);
      }
    } catch (error) {
      // Im Fehlerfall leeres Array belassen; UI fällt auf participants-View zurück
    }
  };

  // Lade Daten neu wenn sich Filter ändern
  useEffect(() => {
    loadResultsData();
  }, [selectedCategory, selectedAgeGroup, selectedGender, selectedLocation]);

  // SSE-Verbindungen für aktive Gruppen einrichten
  useEffect(() => {
    // Finde alle aktiven Gruppen aus scheduleData (nicht nur resultsData, da aktive Events möglicherweise noch keine Ergebnisse haben)
    const activeGroups = new Set();
    scheduleData.forEach(event => {
      if (event.status === 'Aktiv' && event.group?.id) {
        activeGroups.add(event.group.id);
      }
    });
    
    // Füge auch aktive Gruppen aus resultsData hinzu (falls vorhanden)
    resultsData.forEach(event => {
      if (event.status === 'Aktiv' && event.group?.id) {
        activeGroups.add(event.group.id);
      }
    });

    // Trenne Verbindungen für Gruppen, die nicht mehr aktiv sind
    activeGroupConnectionsRef.current.forEach((cleanup, groupId) => {
      if (!activeGroups.has(groupId)) {
        cleanup();
        activeGroupConnectionsRef.current.delete(groupId);
      }
    });

    // Erstelle neue Verbindungen für aktive Gruppen
    activeGroups.forEach(groupId => {
      if (!activeGroupConnectionsRef.current.has(groupId)) {
        // Handler für SSE-Updates
        const handleUpdate = (update) => {
          console.log('SSE: Update empfangen für Gruppe', groupId, 'Typ:', update.type, 'Update:', update);
          
          if (update.type === 'error') {
            console.error('SSE-Fehler:', update.message);
            return;
          }

          if (update.type === 'connected') {
            console.log('SSE-Verbindung hergestellt für Gruppe', update.groupId);
            return;
          }
          
          if (update.type === 'heartbeat') {
            // Heartbeat ignorieren
            return;
          }

          if (update.type === 'result-update') {
            // Einzelnes Result-Update verarbeiten
            const result = update.result;
            const participantId = result.participantId || (result.nationId ? `nation-${result.nationId}` : null);
            
            console.log('SSE: Result-Update empfangen für Gruppe', groupId, 'Teilnehmer', participantId, 'Result:', result);
            console.log('SSE: Result-Update Details:', {
              judgeScore1: result.judgeScore1,
              judgeScore2: result.judgeScore2,
              judgeScore3: result.judgeScore3,
              judgeScore4: result.judgeScore4,
              judgeScore5: result.judgeScore5,
              penalty: result.penalty,
              rank: result.rank
            });
            
            // Lade Ergebnisse für diese Gruppe neu, um sicherzustellen, dass alle Daten synchronisiert sind
            resultsService.getResultsByGroup(groupId).then(resultsResult => {
              if (resultsResult.success && resultsResult.data) {
                console.log('SSE: Ergebnisse für Gruppe neu geladen:', resultsResult.data.length, 'Ergebnisse');
                // Aktualisiere resultsData mit den neu geladenen Daten
                setResultsData(prevData => {
                  const newData = prevData.map(event => {
                    const eventGroupId = event.id || event.group?.id;
                    if (eventGroupId === groupId) {
                      // Erstelle Teilnehmer-Map aus den geladenen Ergebnissen
                      const participantsMap = new Map();
                      resultsResult.data.forEach(r => {
                        const pid = r.participantId || (r.nationId ? `nation-${r.nationId}` : null);
                        if (pid) {
                          const judgeScores = [
                            parseFloat(r.judgeScore1) || 0,
                            parseFloat(r.judgeScore2) || 0,
                            parseFloat(r.judgeScore3) || 0,
                            parseFloat(r.judgeScore4) || 0,
                            parseFloat(r.judgeScore5) || 0
                          ];
                          const penalty = parseFloat(r.penalty) || 0;
                          const validScores = judgeScores.filter(score => score > 0);
                          const averageScore = validScores.length > 0
                            ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
                            : 0;
                          const finalScore = averageScore + penalty;
                          
                          participantsMap.set(pid, {
                            judgeScores,
                            penalty,
                            score: finalScore.toFixed(2),
                            rank: r.rank || null,
                            didNotStart: r.didNotStart || false,
                            isLate: r.isLate || false
                          });
                        }
                      });
                      
                      // Aktualisiere Teilnehmer mit den neuen Daten
                      const updatedParticipants = (event.participants || []).map(p => {
                        const updatedData = participantsMap.get(p.id);
                        if (updatedData) {
                          return {
                            ...p,
                            ...updatedData
                          };
                        }
                        return p;
                      });
                      
                      return {
                        ...event,
                        participants: updatedParticipants
                      };
                    }
                    return event;
                  });
                  
                  return newData;
                });
              }
            }).catch(err => {
              console.error('SSE: Fehler beim Neuladen der Ergebnisse:', err);
            });
            
            // Aktualisiere letzte Aktualisierungszeit
            setLastUpdateTimes(prev => {
              const newMap = new Map(prev);
              newMap.set(groupId, new Date());
              return newMap;
            });
            
            if (participantId) {
              // Aktualisiere resultsData
              setResultsData(prevData => {
                console.log('SSE: Aktualisiere resultsData, vorher:', prevData.length, 'Events');
                
                // Prüfe ob Event bereits existiert
                let eventExists = prevData.some(e => {
                  const eventGroupId = e.id || e.group?.id;
                  return eventGroupId === groupId;
                });
                console.log('SSE: Event existiert in resultsData:', eventExists, 'groupId:', groupId);
                
                // Wenn Event nicht existiert, erstelle es aus scheduleData
                if (!eventExists) {
                  const scheduleEvent = scheduleData.find(e => {
                    const eventGroupId = e.group?.id || e.id;
                    return eventGroupId === groupId && e.status === 'Aktiv';
                  });
                  
                  if (scheduleEvent) {
                    console.log('SSE: Event nicht in resultsData gefunden, erstelle aus scheduleData');
                    // Lade Teilnehmer für dieses Event
                    const eventParticipants = getParticipantsForEvent(scheduleEvent);
                    const newEvent = {
                      id: groupId,
                      group: scheduleEvent.group,
                      status: 'Aktiv',
                      venue: scheduleEvent.venue,
                      participants: eventParticipants.map(p => ({
                        id: p.id,
                        name: p.name,
                        nation: p.nation?.name || 'Unbekannt',
                        nationCode: p.nation?.code || 'DE',
                        judgeScores: [0, 0, 0, 0, 0],
                        penalty: 0,
                        score: 0,
                        rank: 0
                      }))
                    };
                    
                    // Füge das neue Event zu prevData hinzu
                    const dataWithNewEvent = [...prevData, newEvent];
                    prevData = dataWithNewEvent;
                    eventExists = true;
                    console.log('SSE: Neues Event erstellt, Teilnehmer:', newEvent.participants.length);
                  }
                }
                
                const newData = prevData.map(event => {
                  const eventGroupId = event.id || event.group?.id;
                  if (eventGroupId === groupId && event.participants) {
                    console.log('SSE: Event gefunden, Teilnehmer:', event.participants.length);
                    const updatedParticipants = event.participants.map(p => {
                      const matches = p.id === participantId || (p.nationId && `nation-${p.nationId}` === participantId);
                      if (matches) {
                        console.log('SSE: Teilnehmer gefunden und aktualisiert:', p.id, '->', participantId);
                        const judgeScores = [
                          parseFloat(result.judgeScore1) || 0,
                          parseFloat(result.judgeScore2) || 0,
                          parseFloat(result.judgeScore3) || 0,
                          parseFloat(result.judgeScore4) || 0,
                          parseFloat(result.judgeScore5) || 0
                        ];
                        const penalty = parseFloat(result.penalty) || 0;
                        const validScores = judgeScores.filter(score => score > 0);
                        const averageScore = validScores.length > 0
                          ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
                          : 0;
                        const finalScore = averageScore + penalty;

                        const oldScore = p.score;
                        const oldJudgeScores = p.judgeScores || [null, null, null, null, null];
                        
                        // Markiere geänderte Werte für Animation
                        const changedKeys = new Set();
                        if (oldScore !== finalScore.toFixed(2)) {
                          changedKeys.add(`score-${participantId}-${event.id}`);
                        }
                        judgeScores.forEach((score, idx) => {
                          const oldScore = oldJudgeScores[idx];
                          if (oldScore === undefined || oldScore === null || oldScore !== score) {
                            changedKeys.add(`judgeScore${idx + 1}-${participantId}-${event.id}`);
                          }
                        });
                        if (p.penalty !== penalty) {
                          changedKeys.add(`penalty-${participantId}-${event.id}`);
                        }
                        if (p.rank !== (result.rank || null)) {
                          changedKeys.add(`rank-${participantId}-${event.id}`);
                        }
                        
                        if (changedKeys.size > 0) {
                          setUpdatedValues(prev => {
                            const newSet = new Set(prev);
                            changedKeys.forEach(key => newSet.add(key));
                            setTimeout(() => {
                              setUpdatedValues(current => {
                                const updated = new Set(current);
                                changedKeys.forEach(key => updated.delete(key));
                                return updated;
                              });
                            }, 500);
                            return newSet;
                          });
                        }

                        return {
                          ...p,
                          judgeScores,
                          penalty,
                          score: finalScore.toFixed(2),
                          rank: result.rank || null,
                          didNotStart: result.didNotStart || false,
                          isLate: result.isLate || false
                        };
                      }
                      return p;
                    });
                    
                    // Prüfe ob Teilnehmer gefunden wurde
                    const participantFound = updatedParticipants.some(p => 
                      p.id === participantId || (p.nationId && `nation-${p.nationId}` === participantId)
                    );
                    if (!participantFound) {
                      console.warn('SSE: Teilnehmer NICHT gefunden! Teilnehmer-ID:', participantId, 'Verfügbare IDs:', event.participants.map(p => p.id));
                    }
                    
                    return {
                      ...event,
                      participants: updatedParticipants
                    };
                  }
                  return event;
                });
                
                console.log('SSE: Neue resultsData:', newData.length, 'Events');
                // Prüfe ob ein Event aktualisiert wurde
                const updatedEvent = newData.find(e => {
                  const eventGroupId = e.id || e.group?.id;
                  return eventGroupId === groupId;
                });
                if (updatedEvent) {
                  const updatedParticipant = updatedEvent.participants.find(p => 
                    p.id === participantId || (p.nationId && `nation-${p.nationId}` === participantId)
                  );
                  if (updatedParticipant) {
                    console.log('SSE: Bestätigung - Teilnehmer aktualisiert:', updatedParticipant.id, 'Score:', updatedParticipant.score);
                  }
                }
                return newData;
              });
            } else {
              console.warn('SSE: Keine participantId gefunden für Update:', result);
            }
          } else if (update.type === 'rank-update') {
            // Rang-Updates für alle Teilnehmer verarbeiten
            console.log('SSE: Rank-Update empfangen für Gruppe', groupId, 'mit', update.results?.length || 0, 'Ergebnissen');
            console.log('SSE: Rank-Update: Erste 2 Ergebnisse:', update.results?.slice(0, 2));
            setResultsData(prevData => {
              console.log('SSE: Rank-Update: Aktualisiere resultsData, vorher:', prevData.length, 'Events');
                // Prüfe ob Event existiert, wenn nicht, erstelle es
                let eventExists = prevData.some(e => {
                  const eventGroupId = e.id || e.group?.id;
                  return eventGroupId === groupId;
                });
                
                if (!eventExists) {
                  const scheduleEvent = scheduleData.find(e => {
                    const eventGroupId = e.group?.id || e.id;
                    return eventGroupId === groupId && e.status === 'Aktiv';
                  });
                  
                  if (scheduleEvent) {
                    console.log('SSE: Rank-Update: Event nicht in resultsData gefunden, erstelle aus scheduleData');
                    const eventParticipants = getParticipantsForEvent(scheduleEvent);
                    const newEvent = {
                      id: groupId,
                      group: scheduleEvent.group,
                      status: 'Aktiv',
                      venue: scheduleEvent.venue,
                      participants: eventParticipants.map(p => ({
                        id: p.id,
                        name: p.name,
                        nation: p.nation?.name || 'Unbekannt',
                        nationCode: p.nation?.code || 'DE',
                        judgeScores: [0, 0, 0, 0, 0],
                        penalty: 0,
                        score: 0,
                        rank: 0
                      }))
                    };
                    prevData = [...prevData, newEvent];
                    eventExists = true;
                  }
                }
                
                const newData = prevData.map(event => {
                  const eventGroupId = event.id || event.group?.id;
                  if (eventGroupId === groupId && event.participants) {
                    console.log('SSE: Rank-Update: Event gefunden, Teilnehmer:', event.participants.length);
                    const updatedParticipants = event.participants.map(p => {
                      const result = update.results.find(r => 
                        (r.participantId && r.participantId === p.id) || 
                        (r.nationId && `nation-${r.nationId}` === `nation-${p.nationId}`)
                      );
                      
                      if (result) {
                        // Aktualisiere auch Scores, falls vorhanden (rank-update enthält alle Ergebnisdaten)
                        const judgeScores = [
                          parseFloat(result.judgeScore1) || 0,
                          parseFloat(result.judgeScore2) || 0,
                          parseFloat(result.judgeScore3) || 0,
                          parseFloat(result.judgeScore4) || 0,
                          parseFloat(result.judgeScore5) || 0
                        ];
                        const penalty = parseFloat(result.penalty) || 0;
                        const validScores = judgeScores.filter(score => score > 0);
                        const averageScore = validScores.length > 0
                          ? validScores.reduce((sum, s) => sum + s, 0) / validScores.length
                          : 0;
                        const finalScore = averageScore + penalty;
                        
                        const oldRank = p.rank;
                        const newRank = result.rank || null;
                        const oldScore = p.score;
                        const oldJudgeScores = p.judgeScores || [null, null, null, null, null];
                        
                        console.log('SSE: Rank-Update: Teilnehmer', p.id, 'Rang:', oldRank, '->', newRank, 'Score:', oldScore, '->', finalScore.toFixed(2));
                        
                        // Markiere geänderte Werte für Animation
                        const changedKeys = new Set();
                        if (oldRank !== newRank) {
                          changedKeys.add(`rank-${p.id}-${event.id}`);
                        }
                        if (oldScore !== finalScore.toFixed(2)) {
                          changedKeys.add(`score-${p.id}-${event.id}`);
                        }
                        judgeScores.forEach((score, idx) => {
                          const oldScore = oldJudgeScores[idx];
                          if (oldScore === undefined || oldScore === null || oldScore !== score) {
                            changedKeys.add(`judgeScore${idx + 1}-${p.id}-${event.id}`);
                          }
                        });
                        if (p.penalty !== penalty) {
                          changedKeys.add(`penalty-${p.id}-${event.id}`);
                        }
                        
                        if (changedKeys.size > 0) {
                          setUpdatedValues(prev => {
                            const newSet = new Set(prev);
                            changedKeys.forEach(key => newSet.add(key));
                            setTimeout(() => {
                              setUpdatedValues(current => {
                                const updated = new Set(current);
                                changedKeys.forEach(key => updated.delete(key));
                                return updated;
                              });
                            }, 500);
                            return newSet;
                          });
                        }
                        
                        return {
                          ...p,
                          judgeScores,
                          penalty,
                          score: finalScore.toFixed(2),
                          rank: newRank,
                          didNotStart: result.didNotStart || false,
                          isLate: result.isLate || false
                        };
                      }
                      return p;
                    });
                    
                    return {
                      ...event,
                      participants: updatedParticipants
                    };
                  }
                  return event;
                });
                
                console.log('SSE: Rank-Update: Neue resultsData:', newData.length, 'Events');
                // Debug: Zeige Event-Struktur
                const updatedEvent = newData.find(e => {
                  const eventGroupId = e.id || e.group?.id;
                  return eventGroupId === groupId;
                });
                if (updatedEvent) {
                  console.log('SSE: Rank-Update: Aktualisiertes Event:', {
                    id: updatedEvent.id,
                    groupId: updatedEvent.group?.id,
                    participantsCount: updatedEvent.participants?.length,
                    firstParticipant: updatedEvent.participants?.[0] ? {
                      id: updatedEvent.participants[0].id,
                      score: updatedEvent.participants[0].score,
                      rank: updatedEvent.participants[0].rank
                    } : null
                  });
                }
                return newData;
            });
            
            // Aktualisiere letzte Aktualisierungszeit
            setLastUpdateTimes(prev => {
              const newMap = new Map(prev);
              newMap.set(groupId, new Date());
              return newMap;
            });
          }
        };

        // Verbindung herstellen
        console.log(`SSE: Verbinde mit Gruppe ${groupId}`);
        resultsStreamService.connect(groupId, handleUpdate);
        
        // Cleanup-Funktion speichern
        activeGroupConnectionsRef.current.set(groupId, () => {
          resultsStreamService.disconnectGroup(groupId);
        });
      }
    });

    // Cleanup bei Unmount
    return () => {
      activeGroupConnectionsRef.current.forEach((cleanup) => cleanup());
      activeGroupConnectionsRef.current.clear();
    };
  }, [resultsData, scheduleData, participants]);

  // Hilfsfunktionen für Schedule-ähnliche Anzeige
  const getParticipantsForEvent = (event) => {
    if (!event.group) return [];
    
    // Spezielle Behandlung für Team-Events (Gruppenwettbewerb)
    const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
    
    if (isTeamEvent && teamData && teamData.teams) {
      // Sammle alle Teilnehmer aus allen Teams
      const allTeamParticipants = [];
      teamData.teams.forEach(team => {
        if (team.participants && Array.isArray(team.participants)) {
          allTeamParticipants.push(...team.participants);
        }
      });
      return allTeamParticipants;
    }
    
    // Normale Gruppen: Filtere über ParticipantGroup
    if (!participants.length) return [];
    
    const eventParticipants = participants.filter(participant => {
      if (!participant.groups || !Array.isArray(participant.groups)) {
        return false;
      }
      
      return participant.groups.some(group => group.id === event.group.id);
    });
    
    eventParticipants.sort((a, b) => {
      const aGroup = a.groups.find(g => g.id === event.group.id);
      const bGroup = b.groups.find(g => g.id === event.group.id);
      const aStartNumber = aGroup ? aGroup.startNumber : 999;
      const bStartNumber = bGroup ? bGroup.startNumber : 999;
      return aStartNumber - bStartNumber;
    });
    
    return eventParticipants;
  };

  const calculateGroupDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '0min';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  };

  // Funktion zum Rendern der Teilnehmerliste (wie im Schedule)
  const renderParticipantsList = (event) => {
    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
    const isTeamEvent = !!(eventInSchedule?.group?.isTeamCompetition || eventInSchedule?.group?.id === 'team-competition' || eventInSchedule?.group?.name === 'Gruppenwettbewerb');
    
    // Spezielle Darstellung für Team-Events (Gruppenwettbewerb)
    if (isTeamEvent && teamData && teamData.teams) {
      return (
        <div className="mt-4 space-y-4">
          <h5 className="text-sm font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
            Teams ({teamData.teams.length} Nationen, {teamData.currentParticipants || 0} Teilnehmer)
          </h5>
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="divide-y divide-slate-200 dark:divide-slate-700">
              {teamData.teams.map((team) => {
                const isExpanded = expandedNations.has(team.nationId);
                return (
                  <div key={team.nationId} className="transition-colors">
                    {/* Nationen-Header (ausklappbar) */}
                    <button
                      onClick={() => handleToggleNationExpansion(team.nationId)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                          <i className="fas fa-chevron-right text-slate-400 dark:text-slate-500 text-sm"></i>
                        </div>
                        {team.startNumber && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary dark:bg-primary-light text-white">
                            #{team.startNumber}
                          </span>
                        )}
                        <img
                          src={process.env.PUBLIC_URL + "/images/flags/" + (team.nationCode || 'DE') + ".png"}
                          alt={team.nationName}
                          className="w-8 h-8 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{team.nationName}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{team.teamSize} Teilnehmer</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                          <i className="fas fa-users text-[10px]"></i>
                          Team
                        </span>
                      </div>
                    </button>

                    {/* Ausklappbare Teilnehmer-Liste */}
                    {isExpanded && team.participants && (
                      <div className="bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                        <div className="px-6 py-4">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                              <thead className="bg-white dark:bg-slate-800">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">#</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Teilnehmer</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Alter</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Geschlecht</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                                {team.participants.map((participant, index) => (
                                  <tr key={participant.id} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                                      <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                                        {participant.participantNumber || index + 1}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">{participant.name}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{participant.age || '-'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                      {participant.gender === 'männlich' ? 'M' : participant.gender === 'weiblich' ? 'W' : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
    
    // Normale Teilnehmerliste für reguläre Events
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {event.participants.map((participant) => {
            const nationCode = participant.nation?.code || participant.nationCode || 'DE';
            const nationName = participant.nation?.name || participant.nation || '';

            return (
              <div
                key={participant.id}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center min-w-[2rem] h-6 px-1.5 rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs font-semibold">
                    #{participant.participantNumber || participant.id}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate">
                      {participant.name || participant.id}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {nationCode && (
                        <>
                          <img
                            src={process.env.PUBLIC_URL + "/images/flags/" + nationCode + ".png"}
                            alt={nationName}
                            className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                          />
                          <span className="truncate">{nationName}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    return date.toLocaleTimeString('de-DE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('de-DE', options);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "Ausstehend":
        return t("admin.schedule.pending", "Ausstehend");
      case "Aktiv":
        return t("admin.schedule.active", "Aktiv");
      case "Abgeschlossen":
        return t("admin.schedule.completed", "Abgeschlossen");
      case "Vorbereitung":
        return t("admin.schedule.preparation", "Vorbereitung");
      default:
        return status;
    }
  };

  const handleToggleEventExpand = async (eventId) => {
    const newExpandedEvents = new Set(expandedEvents);
    const event = scheduleData.find(e => (e.group?.id || e.id) === eventId);
    const isTeamEvent = !!(event?.group?.isTeamCompetition || event?.group?.id === 'team-competition' || event?.group?.name === 'Gruppenwettbewerb');
    
    if (newExpandedEvents.has(eventId)) {
      newExpandedEvents.delete(eventId);
      // Lösche nur erweiterte Nationen, aber behalte teamData
      setExpandedNations(new Set());
    } else {
      newExpandedEvents.add(eventId);
      // Lade Team-Daten wenn Team-Event erweitert wird (nur wenn noch nicht vorhanden)
      if (isTeamEvent && !teamData) {
        try {
          const teamGroupResponse = await groupsService.getTeamCompetitionGroup();
          if (teamGroupResponse.success && teamGroupResponse.data) {
            setTeamData(teamGroupResponse.data);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Team-Daten:', error);
        }
      }
    }
    setExpandedEvents(newExpandedEvents);
  };

  const handleToggleNationExpansion = (nationId) => {
    const newExpandedNations = new Set(expandedNations);
    if (newExpandedNations.has(nationId)) {
      newExpandedNations.delete(nationId);
    } else {
      newExpandedNations.add(nationId);
    }
    setExpandedNations(newExpandedNations);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleAgeGroupChange = (ageGroup) => {
    setSelectedAgeGroup(ageGroup);
  };

  const handleGenderChange = (gender) => {
    setSelectedGender(gender);
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
  };

  const handleToggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      console.log("Speichere Ergebnisse:", resultsData);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setIsEditMode(false);
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEventEdit = (eventId) => {
    if (editingEventId === eventId) {
      setEditingEventId(null);
    } else {
      setEditingEventId(eventId);
    }
  };

  const handleSaveGroupChanges = async (groupId) => {
    const group = combinedData.find(e => e.id === groupId);
    if (!group) {
      addToast('error', 'Gruppe nicht gefunden');
      return;
    }
    if (!isEventComplete(group)) {
      addToast('warning', 'Bitte tragen Sie alle Jury-Punkte und Penalties für alle Teilnehmer ein, bevor Sie speichern.');
      return;
    }
    setSavingEventId(groupId);
    try {
      let participantsToUpdate = group.participants;
      const editedGroupData = editedData.get(groupId);
      if (editedGroupData) {
        participantsToUpdate = group.participants.map(participant => {
          const editedParticipant = editedGroupData.participants[participant.id];
          if (editedParticipant) {
            return {
              ...participant,
              judgeScores: editedParticipant.judgeScores,
              penalty: editedParticipant.penalty,
              score: editedParticipant.score
            };
          }
          return participant;
        });
      }

      // Sortiere Teilnehmer nach Punkten und weise Ränge zu
      const sortedParticipants = [...participantsToUpdate]
        .sort((a, b) => {
          if (!a.score || isNaN(a.score)) return 1;
          if (!b.score || isNaN(b.score)) return -1;
          return b.score - a.score;
        })
        .map((participant, index) => ({
          ...participant,
          rank: index + 1
        }));

      // Aktualisiere die lokalen Daten
      setResultsData(prevResults => {
        const updatedResults = [...prevResults];
        const existingResultIndex = updatedResults.findIndex(r => r.id === groupId);
        
        if (existingResultIndex >= 0) {
          // Aktualisiere bestehendes Ergebnis
          updatedResults[existingResultIndex] = {
            ...updatedResults[existingResultIndex],
            gender: group.gender || updatedResults[existingResultIndex].gender,
            participants: sortedParticipants
          };
        } else {
          // Erstelle neues Ergebnis
          updatedResults.push({
            id: groupId,
            eventName: group.eventName,
            location: group.location,
            category: group.category,
            ageGroup: group.ageGroup,
            gender: group.gender,
            participants: sortedParticipants
          });
        }
        
        return updatedResults;
      });

      // Versuche API-Call
      const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === groupId);
      const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' || 
                            eventInSchedule?.group?.isTeamCompetition || 
                            eventInSchedule?.group?.id === 'team-competition');
      
      const resultsToUpdate = sortedParticipants.map(participant => {
        const resultData = {
          judgeScores: participant.judgeScores || [0, 0, 0, 0, 0],
          penalty: participant.penalty || 0
        };
        
        // Konvertiere participant.id zu String für sichere String-Operationen
        const participantIdStr = participant.id ? String(participant.id) : null;
        
        // Bei Team-Events: Extrahiere nationId aus participant.id (Format: "nation-{nationId}")
        if (isTeamEvent) {
          if (participantIdStr && participantIdStr.startsWith('nation-')) {
            // Format: "nation-{nationId}"
            const nationId = parseInt(participantIdStr.replace('nation-', ''));
            resultData.nationId = nationId;
            resultData.participantId = null;
          } else if (participant.nationId) {
            // Fallback: Wenn nationId direkt vorhanden ist
            resultData.nationId = participant.nationId;
            resultData.participantId = null;
          } else if (participant.isTeam) {
            // Team-Participant ohne nation- Prefix, aber mit nationId im Objekt
            resultData.nationId = participant.nationId || participant.id;
            resultData.participantId = null;
          } else {
            console.warn('Team-Event, aber keine nationId gefunden für:', participant);
            resultData.nationId = null;
            resultData.participantId = null;
          }
        } else {
          // Normales Event: Verwende participantId
          if (participantIdStr && !participantIdStr.startsWith('nation-')) {
            resultData.participantId = parseInt(participantIdStr) || participant.id;
            resultData.nationId = null;
          } else if (participant.id) {
            resultData.participantId = participant.id;
            resultData.nationId = null;
          } else {
            console.warn('Normales Event, aber keine participantId gefunden für:', participant);
            resultData.participantId = null;
            resultData.nationId = null;
          }
        }
        
        return resultData;
      });
      
      try {
        console.log('Sende Ergebnisse an Backend:', { groupId, resultsToUpdate, isTeamEvent });
        const result = await resultsService.updateGroupResults(groupId, resultsToUpdate);
        
        if (!result || !result.success) {
          const errorMessage = result?.message || result?.error || 'Unbekannter Fehler beim Speichern';
          console.error('Backend-Fehler:', errorMessage);
          addToast('error', `Fehler beim Speichern: ${errorMessage}`);
          setSavingEventId(null);
          return;
        }
        
        console.log('Ergebnisse erfolgreich an Backend gesendet');
        // Daten neu laden, damit alles aktualisiert ist
        await loadAllData();
        
        setEditingEventId(null);
        setEditedData(prevEdited => {
          const newEdited = new Map(prevEdited);
          newEdited.delete(groupId);
          return newEdited;
        });
        
        addToast('success', 'Ergebnisse erfolgreich gespeichert! Die Gruppe wurde als abgeschlossen markiert.');
      } catch (apiError) {
        console.error('API-Call fehlgeschlagen:', apiError);
        addToast('error', `Fehler beim Speichern: ${apiError.message || 'Verbindungsfehler'}`);
        setSavingEventId(null);
        return;
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      addToast('error', `Fehler beim Speichern: ${error.message}`);
    } finally {
      setSavingEventId(null);
    }
  };

  const handleCancelEventEdit = (eventId) => {
    setResultsData(prevResults => prevResults.map(event => {
      if (event.id === eventId) {
        return event;
      }
      return event;
    }));
    setEditingEventId(null);
  };

  const handleScoreChange = (eventId, participantId, judgeIndex, newScore) => {
    let score = isNaN(newScore) ? 0 : newScore;
    if (score > 10) {
      addToast('error', 'Wertungsrichter-Punkte dürfen maximal 10.00 betragen');
      score = 10;
    }
    
    setEditedData(prevEdited => {
      const newEdited = new Map(prevEdited);
      const eventData = newEdited.get(eventId) || { participants: {} };
      
      const originalEvent = combinedData.find(e => e.id === eventId);
      const originalParticipant = originalEvent?.participants.find(p => p.id === participantId);
      
      if (!eventData.participants[participantId]) {
        eventData.participants[participantId] = {
          judgeScores: [...(originalParticipant?.judgeScores || [0, 0, 0, 0, 0])],
          penalty: originalParticipant?.penalty || 0
        };
      }
      
      eventData.participants[participantId].judgeScores[judgeIndex] = score;
      
      const judgeScores = eventData.participants[participantId].judgeScores;
      const averageScore = parseFloat((judgeScores.reduce((sum, s) => sum + s, 0) / judgeScores.length).toFixed(2));
      const penalty = eventData.participants[participantId].penalty || 0;
      const finalScore = parseFloat((averageScore + penalty).toFixed(2));
      
      eventData.participants[participantId].score = finalScore;
      
      newEdited.set(eventId, eventData);
      return newEdited;
    });
    
    setResultsData(prevResults => prevResults.map(event => {
      if (event.id !== eventId) return event;
      
      const updatedParticipants = event.participants.map(p => {
        if (p.id === participantId) {
          const updatedJudgeScores = [...p.judgeScores];
          updatedJudgeScores[judgeIndex] = score;
          
          const averageScore = parseFloat((updatedJudgeScores.reduce((sum, s) => sum + s, 0) / updatedJudgeScores.length).toFixed(2));
          const finalScore = parseFloat((averageScore + p.penalty).toFixed(2));
          
          return {
            ...p,
            judgeScores: updatedJudgeScores,
            score: finalScore
          };
        }
        return p;
      });
      
      const sortedParticipants = [...updatedParticipants]
        .sort((a, b) => {
          if (!a.score || isNaN(a.score)) return 1;
          if (!b.score || isNaN(b.score)) return -1;
          return b.score - a.score;
        })
        .map((participant, index) => ({
          ...participant,
          rank: index + 1
        }));
      
      const finalParticipants = updatedParticipants.map(participant => {
        const sortedParticipant = sortedParticipants.find(p => p.id === participant.id);
        return {
          ...participant,
          rank: sortedParticipant ? sortedParticipant.rank : participant.rank
        };
      });
      
      return {
        ...event,
        participants: finalParticipants
      };
    }));
  };

  const handlePenaltyChange = (eventId, participantId, newPenalty) => {
    const penalty = isNaN(newPenalty) ? 0 : newPenalty;
    
    setEditedData(prevEdited => {
      const newEdited = new Map(prevEdited);
      const eventData = newEdited.get(eventId) || { participants: {} };
      
      const originalEvent = combinedData.find(e => e.id === eventId);
      const originalParticipant = originalEvent?.participants.find(p => p.id === participantId);
      
      if (!eventData.participants[participantId]) {
        eventData.participants[participantId] = {
          judgeScores: [...(originalParticipant?.judgeScores || [0, 0, 0, 0, 0])],
          penalty: originalParticipant?.penalty || 0
        };
      }
      
      eventData.participants[participantId].penalty = penalty;
      
      const judgeScores = eventData.participants[participantId].judgeScores;
      const averageScore = parseFloat((judgeScores.reduce((sum, s) => sum + s, 0) / judgeScores.length).toFixed(2));
      const finalScore = parseFloat((averageScore + penalty).toFixed(2));
      
      eventData.participants[participantId].score = finalScore;
      
      newEdited.set(eventId, eventData);
      return newEdited;
    });
    
    setResultsData(prevResults => prevResults.map(event => {
      if (event.id !== eventId) return event;
      
      const updatedParticipants = event.participants.map(p => {
        if (p.id === participantId) {
          const averageScore = parseFloat((p.judgeScores.reduce((sum, s) => sum + s, 0) / p.judgeScores.length).toFixed(2));
          const finalScore = parseFloat((averageScore + penalty).toFixed(2));
          
          return {
            ...p,
            penalty: penalty,
            score: finalScore
          };
        }
        return p;
      });
      
      const sortedParticipants = [...updatedParticipants]
        .sort((a, b) => {
          if (!a.score || isNaN(a.score)) return 1;
          if (!b.score || isNaN(b.score)) return -1;
          return b.score - a.score;
        })
        .map((participant, index) => ({
          ...participant,
          rank: index + 1
        }));
      
      const finalParticipants = updatedParticipants.map(participant => {
        const sortedParticipant = sortedParticipants.find(p => p.id === participant.id);
        return {
          ...participant,
          rank: sortedParticipant ? sortedParticipant.rank : participant.rank
        };
      });
      
      return {
        ...event,
        participants: finalParticipants
      };
    }));
  };

  const handleToggleJudgeScores = (participantId) => {
    const newExpandedJudgeScores = new Set(expandedJudgeScores);
    if (newExpandedJudgeScores.has(participantId)) {
      newExpandedJudgeScores.delete(participantId);
    } else {
      newExpandedJudgeScores.add(participantId);
    }
    setExpandedJudgeScores(newExpandedJudgeScores);
  };

  const handleToggleAllJudgeScores = (eventId) => {
    const event = combinedData.find(e => e.id === eventId);
    if (!event) return;

    const eventParticipantIds = event.participants.map(p => p.id);
    const allExpanded = eventParticipantIds.every(id => expandedJudgeScores.has(id));

    const newExpandedJudgeScores = new Set(expandedJudgeScores);
    
    if (allExpanded) {
      eventParticipantIds.forEach(id => newExpandedJudgeScores.delete(id));
    } else {
      eventParticipantIds.forEach(id => newExpandedJudgeScores.add(id));
    }
    
    setExpandedJudgeScores(newExpandedJudgeScores);
  };

  // Demo-Ergebnisse für eine Gruppe generieren
  const handleGenerateDemoResults = (eventId) => {
    const event = combinedData.find(e => e.id === eventId);
    if (!event) return;

    // Zufallswerte generieren (7.50 - 9.90), Penalty -0.5 bis +0.5 in 0.1 Schritten
    const randomScore = () => parseFloat((7.5 + Math.random() * 2.4).toFixed(2));
    const randomPenalty = () => {
      const vals = [-0.5,-0.4,-0.3,-0.2,-0.1,0,0.1,0.2,0.3,0.4,0.5];
      return vals[Math.floor(Math.random()*vals.length)];
    };

    // editedData aktualisieren
    setEditedData(prev => {
      const next = new Map(prev);
      const participantsMap = {};
      event.participants.forEach(p => {
        const judgeScores = [randomScore(), randomScore(), randomScore(), randomScore(), randomScore()];
        const penalty = randomPenalty();
        const average = parseFloat((judgeScores.reduce((s,v)=>s+v,0)/judgeScores.length).toFixed(2));
        const score = parseFloat((average + penalty).toFixed(2));
        participantsMap[p.id] = { judgeScores, penalty, score };
      });
      next.set(eventId, { participants: participantsMap });
      return next;
    });

    // resultsData sofort aktualisieren (Preview) inkl. Rangberechnung
    setResultsData(prev => prev.map(ev => {
      if (ev.id !== eventId) return ev;
      const updatedParticipants = ev.participants.map(p => {
        const judgeScores = [randomScore(), randomScore(), randomScore(), randomScore(), randomScore()];
        const penalty = randomPenalty();
        const average = parseFloat((judgeScores.reduce((s,v)=>s+v,0)/judgeScores.length).toFixed(2));
        const score = parseFloat((average + penalty).toFixed(2));
        return { ...p, judgeScores, penalty, score };
      });
      const ranked = [...updatedParticipants]
        .sort((a,b)=> (isNaN(b.score)?-1:b.score) - (isNaN(a.score)?-1:a.score))
        .map((p, idx) => ({ ...p, rank: idx+1 }));
      return { ...ev, participants: ranked };
    }));

    addToast('success', 'Demo-Ergebnisse generiert. Speichern, um sie zu übernehmen.');
  };

  const categoryOptions = [
    { value: "all", label: t("admin.results.allCategories", "Alle Kategorien") },
    ...categories.map((category) => ({
      value: category.name || category,
      label: category.name || category
    }))
  ];

  const ageGroupOptions = [
    { value: "all", label: t("admin.results.allAgeGroups", "Alle Altersgruppen") },
    ...ageGroups
      .filter((group) => group.name !== 'Alle') // "Alle" ausschließen
      .map((group) => ({
        value: group.name,
        label: group.name
      }))
  ];

  const genderOptions = [
    { value: "all", label: t("admin.results.allGenders", "Alle Geschlechter") },
    { value: "männlich", label: "Männlich" },
    { value: "weiblich", label: "Weiblich" },
    { value: "gemischt", label: "Gemischt" }
  ];

  const locationOptions = [
    { value: "all", label: t("admin.results.allLocations", "Alle Flächen") },
    { value: "Fläche A", label: "Fläche A" },
    { value: "Fläche B", label: "Fläche B" }
  ];

  // Kombiniere Schedule- und Results-Daten basierend auf Status
  const combinedData = useMemo(() => {
    console.log('combinedData: Neuberechnung, resultsData:', resultsData.length, 'allResultsData:', allResultsData.length, 'scheduleData:', scheduleData.length);
    const combined = [];
    
    // Erstelle zwei Maps: eine für aktive Events (resultsData mit SSE-Updates) und eine für abgeschlossene Events (allResultsData)
    const activeResultsMap = new Map();
    resultsData.forEach(result => {
      const eventId = result.id || result.group?.id;
      if (eventId) {
        activeResultsMap.set(eventId, result);
      }
    });
    
    const completedResultsMap = new Map();
    allResultsData.forEach(result => {
      const eventId = result.id || result.group?.id;
      if (eventId) {
        completedResultsMap.set(eventId, result);
      }
    });
    
    console.log('combinedData: activeResultsMap:', activeResultsMap.size, 'completedResultsMap:', completedResultsMap.size);
    
    scheduleData.forEach(event => {
      const groupId = event.group?.id || event.id;
      // Für aktive Events: Verwende resultsData (wird durch SSE aktualisiert)
      // Für abgeschlossene Events: Verwende allResultsData (statische Daten)
      const existingResult = event.status === "Aktiv" 
        ? activeResultsMap.get(groupId) 
        : completedResultsMap.get(groupId) || activeResultsMap.get(groupId);
      console.log('combinedData: Verarbeite Event', groupId, 'Status:', event.status, 'existingResult:', !!existingResult);
      
      if (event.status === "Ausstehend" || event.status === "Vorbereitung") {
        combined.push({
          id: groupId,
          type: 'schedule',
          eventName: event.group?.name || 'Unbekanntes Event',
          location: event.venue,
          category: event.group?.category?.name,
          ageGroup: event.group?.ageGroup?.name,
          gender: event.group?.gender,
          status: event.status,
          startTime: event.startTime,
          endTime: event.endTime,
          group: event.group,
          participants: getParticipantsForEvent(event)
        });
      } else if (event.status === "Aktiv") {
        // Prüfe ob es ein Team-Event ist
        const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
        
        let baseResults;
        if (isTeamEvent && teamData && teamData.teams) {
          // Für Team-Events: Gruppiere nach Nationen
          baseResults = teamData.teams.map(team => ({
            id: `nation-${team.nationId}`, // Eindeutige ID für Nation
            name: team.nationName,
            nation: team.nationName,
            nationCode: team.nationCode || 'DE',
            nationId: team.nationId,
            teamSize: team.teamSize,
            judgeScores: [0, 0, 0, 0, 0],
            penalty: 0,
            score: 0,
            rank: 0,
            isTeam: true
          }));
        } else {
          // Normale Events: Einzelne Teilnehmer
          baseResults = getParticipantsForEvent(event).map(participant => ({
            id: participant.id,
            name: participant.name,
            nation: participant.nation?.name || 'Unbekannt',
            nationCode: participant.nation?.code || 'DE',
            judgeScores: [0, 0, 0, 0, 0],
            penalty: 0,
            score: 0,
            rank: 0
          }));
        }
        
        // Übernehme Werte aus resultsData, falls vorhanden (für Live-Updates)
        if (existingResult && existingResult.participants) {
          console.log('combinedData: Übernehme Werte aus existingResult für Gruppe', groupId, 'mit', existingResult.participants.length, 'Teilnehmern');
          const resultsMap = new Map();
          existingResult.participants.forEach(p => {
            resultsMap.set(p.id, p);
          });
          
          baseResults.forEach(participant => {
            const resultParticipant = resultsMap.get(participant.id);
            if (resultParticipant) {
              console.log('combinedData: Aktualisiere Teilnehmer', participant.id, 'Score:', participant.score, '->', resultParticipant.score);
              participant.judgeScores = resultParticipant.judgeScores || [0, 0, 0, 0, 0];
              participant.penalty = resultParticipant.penalty || 0;
              participant.score = resultParticipant.score || 0;
              participant.rank = resultParticipant.rank || 0;
              participant.didNotStart = resultParticipant.didNotStart || false;
              participant.isLate = resultParticipant.isLate || false;
            }
          });
        } else {
          console.log('combinedData: Kein existingResult für Gruppe', groupId);
        }
        
        // Übernehme Werte aus editedData (lokale Änderungen haben Priorität)
        const editedEventData = editedData.get(groupId);
        if (editedEventData) {
          baseResults.forEach(participant => {
            const editedParticipant = editedEventData.participants[participant.id];
            if (editedParticipant) {
              participant.judgeScores = editedParticipant.judgeScores;
              participant.penalty = editedParticipant.penalty;
              participant.score = editedParticipant.score;
            }
          });
        }
        
        combined.push({
          id: groupId,
          type: 'result',
          eventName: event.group?.name || 'Unbekanntes Event',
          location: event.venue,
          category: event.group?.category?.name,
          ageGroup: event.group?.ageGroup?.name,
          gender: event.group?.gender,
          status: event.status,
          participants: baseResults,
          isTeamEvent: isTeamEvent
        });
      } else if (event.status === "Abgeschlossen") {
        if (existingResult) {
          // Prüfe ob es ein Team-Event ist und ob die Teilnehmer noch Nationen sind
          const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
          let participants = existingResult.participants;
          
          // Wenn Team-Event, aber Teilnehmer noch nicht als Nationen strukturiert, konvertiere sie
          if (isTeamEvent && teamData && teamData.teams && participants.length > 0 && !participants[0]?.isTeam) {
            // Gruppiere bestehende Teilnehmer nach Nationen
            const participantsByNation = {};
            participants.forEach(p => {
              const nationName = p.nation || 'Unbekannt';
              if (!participantsByNation[nationName]) {
                participantsByNation[nationName] = [];
              }
              participantsByNation[nationName].push(p);
            });
            
            // Erstelle Nationen-Einträge mit den ersten Punkten pro Nation
            participants = teamData.teams.map(team => {
              const nationParticipants = participantsByNation[team.nationName] || [];
              const firstParticipant = nationParticipants[0];
              return {
                id: `nation-${team.nationId}`,
                name: team.nationName,
                nation: team.nationName,
                nationCode: team.nationCode || 'DE',
                nationId: team.nationId,
                teamSize: team.teamSize,
                judgeScores: firstParticipant?.judgeScores || [0, 0, 0, 0, 0],
                penalty: firstParticipant?.penalty || 0,
                score: firstParticipant?.score || 0,
                rank: firstParticipant?.rank || 0,
                isTeam: true
              };
            });
          }
          
          combined.push({
            id: groupId,
            type: 'result',
            eventName: existingResult.eventName,
            location: existingResult.location,
            category: existingResult.category,
            ageGroup: existingResult.ageGroup,
            gender: existingResult.gender || event.group?.gender || 'Unbekannt',
            status: 'Abgeschlossen',
            participants: participants,
            isTeamEvent: isTeamEvent
          });
        } else {
          // Zeige abgeschlossene Gruppe auch ohne vorhandene Ergebnisdaten (leere Ergebnisliste aus Teilnehmern)
          const isTeamEvent = !!(event.group?.isTeamCompetition || event.group?.id === 'team-competition' || event.group?.name === 'Gruppenwettbewerb');
          
          let emptyResults;
          if (isTeamEvent && teamData && teamData.teams) {
            // Für Team-Events: Gruppiere nach Nationen
            emptyResults = teamData.teams.map(team => ({
              id: `nation-${team.nationId}`,
              name: team.nationName,
              nation: team.nationName,
              nationCode: team.nationCode || 'DE',
              nationId: team.nationId,
              teamSize: team.teamSize,
              judgeScores: [0, 0, 0, 0, 0],
              penalty: 0,
              score: 0,
              rank: 0,
              isTeam: true
            }));
          } else {
            // Normale Events: Einzelne Teilnehmer
            emptyResults = getParticipantsForEvent(event).map(participant => ({
              id: participant.id,
              name: participant.name,
              nation: participant.nation?.name || 'Unbekannt',
              nationCode: participant.nation?.code || 'DE',
              judgeScores: [0, 0, 0, 0, 0],
              penalty: 0,
              score: 0,
              rank: 0
            }));
          }
          
          combined.push({
            id: groupId,
            type: 'result',
            eventName: event.group?.name || 'Unbekanntes Event',
            location: event.venue,
            category: event.group?.category?.name,
            ageGroup: event.group?.ageGroup?.name,
            gender: event.group?.gender,
            status: 'Abgeschlossen',
            participants: emptyResults,
            isTeamEvent: isTeamEvent
          });
        }
      }
    });
    
    // Sortiere combinedData nach sequenceNumber aus scheduleData (Zeitplan-Reihenfolge)
    const scheduleMap = new Map();
    scheduleData.forEach(event => {
      const groupId = event.group?.id || event.id;
      scheduleMap.set(groupId, event);
    });
    
    combined.sort((a, b) => {
      const aEvent = scheduleMap.get(a.id);
      const bEvent = scheduleMap.get(b.id);
      
      if (!aEvent || !bEvent) {
        // Wenn ein Event nicht im Schedule ist, sortiere nach Name
        if (!aEvent && !bEvent) return a.eventName.localeCompare(b.eventName);
        if (!aEvent) return 1;
        if (!bEvent) return -1;
      }
      
      const aSequence = aEvent?.sequenceNumber ?? 999999;
      const bSequence = bEvent?.sequenceNumber ?? 999999;
      
      return aSequence - bSequence;
    });
    
    return combined;
  }, [scheduleData, resultsData, participants, editedData, teamData, allResultsData]);

  // Debug: Reagiere auf Änderungen von resultsData
  useEffect(() => {
    console.log('resultsData geändert:', resultsData.length, 'Events');
    resultsData.forEach(event => {
      const eventGroupId = event.id || event.group?.id;
      if (event.participants && event.participants.length > 0) {
        console.log('Event', eventGroupId, 'hat', event.participants.length, 'Teilnehmer');
        event.participants.slice(0, 3).forEach(p => {
          console.log('  Teilnehmer', p.id, 'Score:', p.score, 'Rank:', p.rank);
        });
      }
    });
  }, [resultsData]);

  // Automatisch alle Wertungsrichter-Punkte für aktive Events ausklappen
  useEffect(() => {
    const newExpandedJudgeScores = new Set(expandedJudgeScores);
    
    const activeEvents = combinedData.filter(event => event.status === 'Aktiv');
    
    activeEvents.forEach(event => {
      event.participants.forEach(participant => {
        newExpandedJudgeScores.add(participant.id);
      });
    });
    
    setExpandedJudgeScores(newExpandedJudgeScores);
  }, [combinedData]);

  // Filtere kombinierte Daten
  const filteredData = combinedData.filter((item) => {
    const matchesSearch = item.eventName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.participants.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Prüfe ob es ein Team-Event ist (überprüfe sowohl combinedData item als auch scheduleData)
    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === item.id);
    const isTeamEvent = !!(item.isTeamCompetition || item.groupId === 'team-competition' || 
                          eventInSchedule?.group?.name === 'Gruppenwettbewerb' || 
                          eventInSchedule?.group?.isTeamCompetition || 
                          eventInSchedule?.group?.id === 'team-competition');
    
    let matchesCategory, matchesAgeGroup, matchesGender;
    
    if (isTeamEvent) {
      // Spezielle Filterlogik für Team-Events - verwende die Kategorie "Gruppenperformance"
      matchesCategory = selectedCategory === "all" || selectedCategory === "Gruppenperformance";
      matchesAgeGroup = selectedAgeGroup === "all" || item.ageGroup === selectedAgeGroup || item.ageGroup === 'Alle';
      matchesGender = selectedGender === "all" || item.gender === selectedGender || (item.gender === 'gemischt' && selectedGender === 'gemischt');
    } else {
      // Normale Filterlogik
      matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
      matchesAgeGroup = selectedAgeGroup === "all" || item.ageGroup === selectedAgeGroup;
      matchesGender = selectedGender === "all" || (item.gender && String(item.gender).toLowerCase() === selectedGender);
    }
    
    const matchesLocation = selectedLocation === "all" || item.location === selectedLocation;
    return matchesSearch && matchesCategory && matchesAgeGroup && matchesGender && matchesLocation;
  });

  // Gruppiere Daten nach Fläche und sortiere nach ursprünglicher Position
  const dataByLocation = {};
  
  // Erstelle eine Map für schnelle Zuordnung von groupId zu sequenceNumber
  const sequenceMap = new Map();
  scheduleData.forEach(event => {
    const groupId = event.group?.id || event.id;
    sequenceMap.set(groupId, event.sequenceNumber ?? 999999);
  });
  
  const sortBySchedulePosition = (a, b) => {
    const aSequence = sequenceMap.get(a.id) ?? 999999;
    const bSequence = sequenceMap.get(b.id) ?? 999999;
    return aSequence - bSequence;
  };
  
  if (selectedLocation === "all") {
    const halle1Events = filteredData.filter(item => item.location === "Fläche A");
    const halle2Events = filteredData.filter(item => item.location === "Fläche B");
    
    dataByLocation["Fläche A"] = halle1Events.sort(sortBySchedulePosition);
    dataByLocation["Fläche B"] = halle2Events.sort(sortBySchedulePosition);
  } else {
    const locationEvents = filteredData.filter(item => item.location === selectedLocation);
    
    dataByLocation[selectedLocation] = locationEvents.sort(sortBySchedulePosition);
  }

  // Berechne Gesamtanzahl der Teilnehmer pro Fläche
  const getTotalParticipants = (events) => {
    return events.reduce((total, event) => {
      const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
      const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' || 
                            eventInSchedule?.group?.isTeamCompetition || 
                            eventInSchedule?.group?.id === 'team-competition');
      
      if (isTeamEvent && teamData && teamData.currentParticipants) {
        return total + teamData.currentParticipants;
      }
      return total + event.participants.length;
    }, 0);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return "🥇";
      case 2: return "🥈";
      case 3: return "🥉";
      default: return `${rank}.`;
    }
  };

  // Prüfe ob alle Ergebnisse für ein Event eingetragen sind
  const isEventComplete = (event) => {
    if (event.status !== 'Aktiv') return true;
    
    return event.participants.every(participant => {
      const hasJudgeScores = participant.judgeScores.every(score => score > 0);
      const hasPenalty = participant.penalty !== undefined && participant.penalty !== null;
      
      return hasJudgeScores && hasPenalty;
    });
  };

  const addToast = (type, message) => {
    console.log('Toast hinzufügen:', type, message);
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "info" });
    }, 5000);
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "info" });
  };

  if (loading) {
    return (
      <main className="pt-24 pb-8 bg-slate-50/50 dark:bg-slate-900 min-h-screen transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-2">
                <div className="h-9 bg-slate-200 dark:bg-slate-700 rounded w-64"></div>
                <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-96"></div>
              </div>
              <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
            </div>
          </div>

          {/* Controls Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <div className="h-[46px] bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              </div>
              <div className="flex gap-2">
                <div className="w-32 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
                <div className="w-32 h-[46px] bg-slate-200 dark:bg-slate-700 rounded-md"></div>
              </div>
            </div>
          </div>

          {/* Filters Skeleton */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6 animate-pulse">
            <div className="space-y-6">
              {[...Array(4)].map((_, i) => (
                <div key={i}>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-3"></div>
                  <div className="flex flex-wrap gap-1.5">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="h-7 bg-slate-200 dark:bg-slate-700 rounded-full w-24"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Results Cards Skeleton - Location Containers */}
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6`}>
            {[...Array(2)].map((_, locationIndex) => (
              <div key={locationIndex} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
                {/* Location Header */}
                <div className="bg-slate-50 dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                      <div className="flex items-center gap-4">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20"></div>
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Events List */}
                <div className="p-6 space-y-4">
                  {[...Array(3)].map((_, eventIndex) => (
                    <div key={eventIndex} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                      {/* Event Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-40"></div>
                        <div className="flex items-center gap-2">
                          <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded-full w-20"></div>
                          <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
                        </div>
                      </div>
                      
                      {/* Event Title */}
                      <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-4"></div>
                      
                      {/* Participants List */}
                      <div className="space-y-3">
                        {[...Array(2)].map((_, participantIndex) => (
                          <div key={participantIndex} className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-700 last:border-0">
                            <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-full flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
                              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
                            </div>
                            <div className="flex gap-2">
                              <div className="h-8 w-12 bg-slate-200 dark:bg-slate-700 rounded"></div>
                              <div className="h-8 w-12 bg-slate-200 dark:bg-slate-700 rounded"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0.7;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideDown {
          from {
            transform: translateY(-20px);
            opacity: 0.7;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform, opacity;
        }
        .animate-slide-down {
          animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform, opacity;
        }
      `}</style>
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t("admin.results.title", "Ergebnisse")}
                </h1>
                <p className="text-slate-600 dark:text-slate-400 mt-2">
                  {t("admin.results.subtitle", "Verwalte alle Wettkampfergebnisse und Rankings.")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{filteredData.length}</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">{t("admin.results.events", "Events")}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder={t("admin.results.search", "Event oder Teilnehmer suchen...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 h-[46px] border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                />
                <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 text-sm"></i>
              </div>

              {/* Info - Nur im Admin-Bereich anzeigen */}
              {!isPublicView && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                    <i className="fas fa-info-circle mr-2"></i>
                    Klicken Sie auf "Bearbeiten" bei einem Event, um die Punkte zu ändern
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Filter Groups */}
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-6 mb-6">
            <div className="space-y-6">
              {/* Category Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.results.category", "Kategorie")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {categoryOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedCategory === option.value ? 'bg-primary dark:bg-primary-light text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                      onClick={() => handleCategoryChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Group Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.results.ageGroup", "Altersgruppe")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {ageGroupOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedAgeGroup === option.value ? 'bg-primary dark:bg-primary-light text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                      onClick={() => handleAgeGroupChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.results.gender", "Geschlecht")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {genderOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedGender === option.value ? 'bg-primary dark:bg-primary-light text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                      onClick={() => handleGenderChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Location Filter */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  {t("admin.results.location", "Fläche")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {locationOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                        selectedLocation === option.value ? 'bg-primary dark:bg-primary-light text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                      }`}
                      onClick={() => handleLocationChange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Results View - Schedule Style */}
          {Object.keys(dataByLocation).length === 0 || Object.values(dataByLocation).every(events => events.length === 0) ? (
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm p-12">
              <div className="text-center py-12">
                <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-trophy text-slate-400 dark:text-slate-500 text-2xl"></i>
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Ergebnisse vorhanden</h3>
                <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
                  {searchTerm || selectedCategory !== "all" || selectedAgeGroup !== "all" || selectedGender !== "all" || selectedLocation !== "all"
                    ? "Versuchen Sie, Ihre Filter zu ändern oder den Suchbegriff anzupassen."
                    : "Es sind noch keine Ergebnisse vorhanden. Ergebnisse werden angezeigt, sobald Wettbewerbe abgeschlossen wurden oder geplant sind."
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className={`${selectedLocation !== "all" ? 'grid grid-cols-1' : 'grid grid-cols-1 lg:grid-cols-2'} gap-6`}>
              {Object.entries(dataByLocation).map(([location, events]) => (
                events.length > 0 && (
                  <div key={location} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                {/* Venue Header */}
                
                <div className="bg-slate-50 dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{location}</h3>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <i className="fas fa-calendar-alt"></i>
                          {events.length} Events
                        </span>
                        <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                          <i className="fas fa-users"></i>
                          {getTotalParticipants(events)} Athleten
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Events Timeline */}
                <div className="p-6">
                  {events.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="bg-slate-100 dark:bg-slate-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <i className="fas fa-trophy text-slate-400 dark:text-slate-500 text-2xl"></i>
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">Keine Events</h3>
                      <p className="text-slate-500 dark:text-slate-400">{t("admin.results.noEvents", "Keine Events an dieser Fläche")}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {events.map((event) => (
                        <div
                          key={event.id}
                          className={`${
                            event.status === 'Aktiv'
                              ? 'bg-green-50/50 dark:bg-green-900/20 border-2 border-green-400 dark:border-green-500 shadow-md'
                              : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700'
                          } rounded-lg p-4 transition-all ${
                            event.status === 'Aktiv' ? 'hover:shadow-lg' : 'hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600'
                          }`}
                        >
                          {/* Event Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                              {event.type === 'schedule' ? (
                                <>
                                  {formatDate(event.startTime)} • {formatTime(event.startTime)} - {formatTime(event.endTime)}
                                </>
                              ) : (
                                <>
                                  {event.status === 'Aktiv' ? 'Live-Ergebnisse' : 'Endergebnis'}
                                  {event.status === 'Aktiv' && (() => {
                                    const groupId = event.group?.id || event.id;
                                    const lastUpdate = lastUpdateTimes.get(groupId);
                                    return lastUpdate ? (
                                      <>
                                        {' • '}
                                        <span className="text-xs">
                                          <i className="fas fa-sync-alt mr-1"></i>
                                          Aktualisiert: {formatTime(lastUpdate.toISOString())}
                                        </span>
                                      </>
                                    ) : null;
                                  })()}
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Status Badge */}
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                event.status === 'Aktiv' ? 'bg-white dark:bg-slate-800 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-600 shadow-sm' :
                                event.status === 'Ausstehend' ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                                event.status === 'Vorbereitung' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' :
                                'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-300'
                              }`}>
                                <i className={`fas ${
                                  event.status === 'Aktiv' ? 'fa-play' : 
                                  event.status === 'Ausstehend' ? 'fa-clock' : 
                                  event.status === 'Vorbereitung' ? 'fa-hourglass-half' :
                                  'fa-check'
                                } mr-1.5`}></i>
                                {getStatusLabel(event.status)}
                              </span>
                              {/* Dauer-Badge nur für schedule-Events */}
                              {event.type === 'schedule' && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                  <i className="fas fa-clock mr-1.5"></i>
                                  {calculateGroupDuration(event.startTime, event.endTime)}
                                </span>
                              )}

                              {/* Action Buttons */}
                              {(event.type === 'result' || event.status === 'Aktiv') && (
                                event.status === 'Aktiv' ? (
                                  <>
                                    {/* Demo- und Speichern-Buttons nur im Admin-Bereich */}
                                    {!isPublicView && (
                                      <>
                                        <button 
                                          className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" 
                                          title="Demo-Ergebnisse generieren"
                                          onClick={() => handleGenerateDemoResults(event.id)}
                                        >
                                          <i className="fas fa-magic text-sm"></i>
                                        </button>
                                        <button 
                                          className={`p-1.5 rounded transition-colors ${
                                            !isEventComplete(event) 
                                              ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed' 
                                              : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'
                                          }`}
                                          title={!isEventComplete(event) ? "Bitte tragen Sie alle Ergebnisse ein" : "Ergebnisse speichern"}
                                          onClick={() => handleSaveGroupChanges(event.id)}
                                          disabled={savingEventId === event.id || !isEventComplete(event)}
                                        >
                                          <i className={`fas ${savingEventId === event.id ? 'fa-spinner fa-spin' : 'fa-save'} text-sm`}></i>
                                        </button>
                                      </>
                                    )}
                                  </>
                                ) : (!isPublicView && editingEventId === event.id) ? (
                                  <>
                                    <button 
                                      className="p-1.5 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" 
                                      title="Änderungen speichern"
                                      onClick={() => handleSaveGroupChanges(event.id)}
                                      disabled={savingEventId === event.id}
                                    >
                                      <i className={`fas ${savingEventId === event.id ? 'fa-spinner fa-spin' : 'fa-save'} text-sm`}></i>
                                    </button>
                                    <button 
                                      className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" 
                                      title="Abbrechen"
                                      onClick={() => handleCancelEventEdit(event.id)}
                                      disabled={savingEventId === event.id}
                                    >
                                      <i className="fas fa-times text-sm"></i>
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    {/* Edit-Button nur im Admin-Bereich anzeigen */}
                                    {!isPublicView && (
                                      <button 
                                        className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" 
                                        title="Punkte bearbeiten"
                                        onClick={() => handleToggleEventEdit(event.id)}
                                      >
                                        <i className="fas fa-edit text-sm"></i>
                                      </button>
                                    )}
                                    {/* Ausklapp-Button für Wertungsrichter-Punkte - nicht bei abgeschlossenen Gruppen (dort im Content-Bereich) */}
                                    {event.status !== 'Abgeschlossen' && (
                                      <button 
                                        className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" 
                                        title="Alle Wertungsrichter-Punkte ein-/ausklappen"
                                        onClick={() => handleToggleAllJudgeScores(event.id)}
                                      >
                                        <i className={`fas ${event.participants.every(p => expandedJudgeScores.has(p.id)) ? 'fa-chevron-up' : 'fa-chevron-down'} text-sm`}></i>
                                      </button>
                                    )}
                                  </>
                                )
                              )}
                            </div>
                          </div>
                          
                          {/* Event Content */}
                          {event.type === 'schedule' ? (
                            <div className="space-y-3">
                              {/* Titel - nur bis zum ersten "-" */}
                              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                {(() => {
                                  const fullName = event.eventName || 'Unbekanntes Event';
                                  const dashIndex = fullName.indexOf('-');
                                  return dashIndex > 0 ? fullName.substring(0, dashIndex).trim() : fullName;
                                })()}
                              </h4>
                            
                              {/* Badges für Altersgruppe und Geschlecht */}
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                  (() => {
                                    const ageGroupName = event.ageGroup;
                                    const colors = getAgeGroupColors(ageGroupName);
                                    const darkModeMap = {
                                      'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                                      'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                                      'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                                      'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                                      'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                                    };
                                    const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                                    return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                                  })()
                                }`}>
                                  {event.ageGroup || 'Unbekannt'}
                                </span>
                                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                  event.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                                }`}>
                                  {event.gender || 'Unbekannt'}
                                </span>
                              </div>
                              
                              {/* Teilnehmeranzahl links, Ausklappen-Button rechts */}
                              <div className="flex items-center justify-between pt-1">
                                <span className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                                  <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
                                  {(() => {
                                    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
                                    const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' || 
                                                          eventInSchedule?.group?.isTeamCompetition || 
                                                          eventInSchedule?.group?.id === 'team-competition');
                                    if (isTeamEvent && teamData && teamData.currentParticipants) {
                                      return `${teamData.currentParticipants} Teilnehmer`;
                                    }
                                    return `${event.participants.length} Teilnehmer`;
                                  })()}
                                </span>
                                <button 
                                  className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 flex items-center gap-1.5 transition-colors px-2 py-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                                  onClick={() => handleToggleEventExpand(event.id)}
                                >
                                  <i className={`fas ${expandedEvents.has(event.id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`}></i>
                                  {expandedEvents.has(event.id) ? "Ausblenden" : "Anzeigen"}
                                </button>
                              </div>
                            </div>
                          ) : event.status === 'Abgeschlossen' ? (
                            <div className="space-y-3">
                              {/* Titel - nur bis zum ersten "-" */}
                              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                {(() => {
                                  const fullName = event.eventName || 'Unbekanntes Event';
                                  const dashIndex = fullName.indexOf('-');
                                  return dashIndex > 0 ? fullName.substring(0, dashIndex).trim() : fullName;
                                })()}
                              </h4>
                            
                              {/* Badges für Altersgruppe und Geschlecht */}
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                  (() => {
                                    const ageGroupName = event.ageGroup;
                                    const colors = getAgeGroupColors(ageGroupName);
                                    const darkModeMap = {
                                      'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                                      'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                                      'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                                      'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                                      'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                                    };
                                    const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                                    return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                                  })()
                                }`}>
                                  {event.ageGroup || 'Unbekannt'}
                                </span>
                                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                  event.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                                }`}>
                                  {event.gender || 'Unbekannt'}
                                </span>
                              </div>
                              
                              {/* Teilnehmeranzahl links, Ausklappen-Button für Wertungsrichter-Punkte rechts */}
                              <div className="flex items-center justify-between pt-1">
                                <span className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                                  <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
                                  {(() => {
                                    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
                                    const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' || 
                                                          eventInSchedule?.group?.isTeamCompetition || 
                                                          eventInSchedule?.group?.id === 'team-competition');
                                    if (isTeamEvent && teamData && teamData.currentParticipants) {
                                      return `${teamData.currentParticipants} Teilnehmer`;
                                    }
                                    return `${event.participants.length} Teilnehmer`;
                                  })()}
                                </span>
                                <button 
                                  className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" 
                                  title="Alle Wertungsrichter-Punkte ein-/ausklappen"
                                  onClick={() => handleToggleAllJudgeScores(event.id)}
                                >
                                  <i className={`fas ${event.participants.every(p => expandedJudgeScores.has(p.id)) ? 'fa-chevron-up' : 'fa-chevron-down'} text-sm`}></i>
                                </button>
                              </div>
                            </div>
                          ) : event.status === 'Aktiv' ? (
                            <div className="space-y-3">
                              {/* Titel - nur bis zum ersten "-" */}
                              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                {(() => {
                                  const fullName = event.eventName || 'Unbekanntes Event';
                                  const dashIndex = fullName.indexOf('-');
                                  return dashIndex > 0 ? fullName.substring(0, dashIndex).trim() : fullName;
                                })()}
                              </h4>
                            
                              {/* Badges für Altersgruppe und Geschlecht */}
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                  (() => {
                                    const ageGroupName = event.ageGroup;
                                    const colors = getAgeGroupColors(ageGroupName);
                                    const darkModeMap = {
                                      'Kinder': 'dark:bg-emerald-900/30 dark:text-emerald-300',
                                      'Jugend': 'dark:bg-blue-900/30 dark:text-blue-300',
                                      'Erwachsene': 'dark:bg-violet-900/30 dark:text-violet-300',
                                      'Senioren': 'dark:bg-amber-900/30 dark:text-amber-300',
                                      'Senioren Plus': 'dark:bg-red-900/30 dark:text-red-300'
                                    };
                                    const darkModeClasses = darkModeMap[ageGroupName] || 'dark:bg-slate-700 dark:text-slate-300';
                                    return `${colors.bgLight} ${colors.text} ${darkModeClasses}`;
                                  })()
                                }`}>
                                  {event.ageGroup || 'Unbekannt'}
                                </span>
                                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                                  event.gender === 'männlich' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-pink-50 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300'
                                }`}>
                                  {event.gender || 'Unbekannt'}
                                </span>
                              </div>
                              
                              {/* Teilnehmeranzahl links, Ausklappen-Button für Wertungsrichter-Punkte rechts */}
                              <div className="flex items-center justify-between pt-1">
                                <span className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-1.5">
                                  <i className="fas fa-users text-slate-500 dark:text-slate-400"></i>
                                  {(() => {
                                    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
                                    const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' || 
                                                          eventInSchedule?.group?.isTeamCompetition || 
                                                          eventInSchedule?.group?.id === 'team-competition');
                                    if (isTeamEvent && teamData && teamData.currentParticipants) {
                                      return `${teamData.currentParticipants} Teilnehmer`;
                                    }
                                    return `${event.participants.length} Teilnehmer`;
                                  })()}
                                </span>
                                <button 
                                  className="p-1.5 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors" 
                                  title="Alle Wertungsrichter-Punkte ein-/ausklappen"
                                  onClick={() => handleToggleAllJudgeScores(event.id)}
                                >
                                  <i className={`fas ${event.participants.every(p => expandedJudgeScores.has(p.id)) ? 'fa-chevron-up' : 'fa-chevron-down'} text-sm`}></i>
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                {event.eventName}
                              </h4>
                              
                              <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-400">
                                <span className="flex items-center gap-1">
                                  <i className="fas fa-users"></i>
                                  {(() => {
                                    const eventInSchedule = scheduleData.find(e => (e.group?.id || e.id) === event.id);
                                    const isTeamEvent = !!(eventInSchedule?.group?.name === 'Gruppenwettbewerb' || 
                                                          eventInSchedule?.group?.isTeamCompetition || 
                                                          eventInSchedule?.group?.id === 'team-competition');
                                    if (isTeamEvent && teamData && teamData.currentParticipants) {
                                      return `${teamData.currentParticipants} Teilnehmer`;
                                    }
                                    return `${event.participants.length} Teilnehmer`;
                                  })()}
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                  {event.ageGroup}
                                </span>
                                <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300">
                                  {event.gender}
                                </span>
                              </div>
                            </div>
                          )}

                            {/* Participants/Results */}
                            <div className="mt-4">
                              {event.type === 'schedule' && (
                                <>
                                  {/* Participants List mit Trennlinie */}
                                  {expandedEvents.has(event.id) && (
                                    <div className="pt-3 mt-3 border-t border-slate-200 dark:border-slate-700">
                                      {/* Header mit Teilnehmerliste */}
                                      <div className="flex items-center justify-between mb-3">
                                        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Teilnehmerliste</span>
                                      </div>
                                      {renderParticipantsList(event)}
                                    </div>
                                  )}
                                </>
                              )}

                              {(event.type === 'result' || event.status === 'Aktiv') && (() => {
                                // Sortiere Teilnehmer: Normale Teilnehmer nach Punkten, dann DNS-Teilnehmer
                                const sortedParticipants = [...event.participants].sort((a, b) => {
                                  const aDNS = a.didNotStart || false;
                                  const bDNS = b.didNotStart || false;
                                  
                                  // DNS immer ganz unten
                                  if (aDNS && !bDNS) return 1;
                                  if (!aDNS && bDNS) return -1;
                                  
                                  // Innerhalb der Kategorien nach Punkten/Rang
                                  if (!aDNS && !bDNS) {
                                    if (a.rank && b.rank) return a.rank - b.rank;
                                    if (a.score !== undefined && b.score !== undefined) {
                                      return (b.score || 0) - (a.score || 0);
                                    }
                                  }
                                  
                                  return 0;
                                });
                                
                                // Track Position-Änderungen für Animationen
                                if (!previousPositionsRef.current.has(event.id)) {
                                  previousPositionsRef.current.set(event.id, new Map());
                                }
                                const prevPositions = previousPositionsRef.current.get(event.id);
                                const currentPositions = new Map();
                                
                                sortedParticipants.forEach((p, index) => {
                                  currentPositions.set(p.id, index);
                                });
                                
                                // Prüfe auf Position-Änderungen
                                const hasPositionChanges = Array.from(currentPositions.entries()).some(([id, pos]) => {
                                  return prevPositions.get(id) !== undefined && prevPositions.get(id) !== pos;
                                });
                                
                                if (hasPositionChanges) {
                                  // Positionen haben sich geändert - aktualisiere Ref nach kurzer Verzögerung für Animation
                                  setTimeout(() => {
                                    previousPositionsRef.current.set(event.id, currentPositions);
                                  }, 50);
                                } else {
                                  previousPositionsRef.current.set(event.id, currentPositions);
                                }
                                
                                // Berechne Position-Änderung für jeden Teilnehmer
                                const getPositionChange = (participantId) => {
                                  const prevPos = prevPositions.get(participantId);
                                  const currentPos = currentPositions.get(participantId);
                                  
                                  if (prevPos === undefined || prevPos === currentPos) {
                                    return null;
                                  }
                                  
                                  return {
                                    from: prevPos,
                                    to: currentPos,
                                    direction: currentPos < prevPos ? 'up' : 'down'
                                  };
                                };
                                
                                const normalParticipants = sortedParticipants.filter(p => !p.didNotStart);
                                const dnsParticipants = sortedParticipants.filter(p => p.didNotStart);
                                
                                return (
                                  <div className="space-y-3">
                                    {/* Normale Teilnehmer */}
                                    {normalParticipants.map((participant) => {
                                      const positionChange = getPositionChange(participant.id);
                                      const isMoving = positionChange !== null;
                                      const animationClass = isMoving 
                                        ? (positionChange.direction === 'up' ? 'animate-slide-up' : 'animate-slide-down')
                                        : '';
                                      
                                      return (
                                      <div
                                        key={participant.id}
                                        className={`${animationClass} ${(event.status === 'Abgeschlossen' || event.status === 'Aktiv')
                                          ? participant.rank === 1
                                            ? 'bg-yellow-50 dark:bg-yellow-900/20'
                                            : participant.rank === 2
                                              ? 'bg-slate-100 dark:bg-slate-700/40'
                                              : participant.rank === 3
                                                ? 'bg-orange-50 dark:bg-orange-900/20'
                                                : 'bg-white dark:bg-slate-800'
                                          : 'bg-slate-50 dark:bg-slate-800'
                                        } ${participant.didNotStart ? 'opacity-50 grayscale' : ''} rounded-lg p-4 border border-slate-200 dark:border-slate-700 transition-colors duration-300`}
                                      >
                                      {/* Athlete/Team Header */}
                                      <div className="flex items-center gap-4 mb-3">
                                        <div className="flex items-center">
                                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-all duration-300 ${
                                            updatedValues.has(`rank-${participant.id}-${event.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30' : ''
                                          } ${
                                            participant.rank === 1 ? 'bg-yellow-200 dark:bg-yellow-800/60 text-yellow-900 dark:text-yellow-200' :
                                            participant.rank === 2 ? 'bg-slate-200 dark:bg-slate-600 text-slate-800 dark:text-slate-200' :
                                            participant.rank === 3 ? 'bg-orange-200 dark:bg-orange-800/60 text-orange-900 dark:text-orange-200' :
                                            'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                                          }`}>
                                            {getRankIcon(participant.rank)}
                                          </span>
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                            {participant.name}
                                            {participant.didNotStart && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                                <i className="fas fa-times-circle mr-1"></i>
                                                {t("results.didNotStart", "DNS")}
                                              </span>
                                            )}
                                            {participant.isLate && !participant.didNotStart && (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                                <i className="fas fa-clock mr-1"></i>
                                                {t("scorekeeper.late", "Verspätet")}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                                            <img
                                              src={process.env.PUBLIC_URL + "/images/flags/" + participant.nationCode + ".png"}
                                              alt={participant.nation}
                                              className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                                            />
                                            <span>{participant.nation}</span>
                                            {participant.isTeam && participant.teamSize && (
                                              <span className="text-xs text-slate-400 dark:text-slate-500">
                                                • {participant.teamSize} Teilnehmer
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        
                                        <div className="text-right">
                                          <div className={`text-lg font-bold text-slate-900 dark:text-slate-100 transition-all duration-300 ${
                                            updatedValues.has(`score-${participant.id}-${event.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded' : ''
                                          }`}>
                                            {participant.score}
                                          </div>
                                          <div className="text-xs text-slate-500 dark:text-slate-400">Punkte</div>
                                        </div>
                                      </div>

                                      {/* Judge Scores - Inline in Athlete Box */}
                                      {expandedJudgeScores.has(participant.id) && (
                                        <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
                                          <h6 className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-3">Wertungsrichter-Punkte</h6>
                                          <div className="flex items-center gap-4">
                                            {participant.judgeScores.map((score, judgeIndex) => (
                                              <div key={judgeIndex} className="flex flex-col items-center">
                                                <span className="text-xs text-slate-500 dark:text-slate-400 mb-1">WR {judgeIndex + 1}</span>
                                                {(!isPublicView && (editingEventId === event.id || event.status === 'Aktiv')) ? (
                                                  <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="10"
                                                    value={score}
                                                    onChange={(e) => {
                                                      const val = parseFloat(e.target.value);
                                                      if (val > 10) {
                                                        e.target.value = 10;
                                                        handleScoreChange(event.id, participant.id, judgeIndex, 10);
                                                      } else {
                                                        handleScoreChange(event.id, participant.id, judgeIndex, val);
                                                      }
                                                    }}
                                                    onBlur={(e) => {
                                                      const val = parseFloat(e.target.value);
                                                      if (isNaN(val) || val < 0) {
                                                        e.target.value = 0;
                                                        handleScoreChange(event.id, participant.id, judgeIndex, 0);
                                                      } else if (val > 10) {
                                                        e.target.value = 10;
                                                        handleScoreChange(event.id, participant.id, judgeIndex, 10);
                                                      }
                                                    }}
                                                    className="w-16 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                                    placeholder="0.00"
                                                  />
                                                ) : (
                                                  <span className={`w-16 text-center text-sm font-semibold text-slate-900 dark:text-slate-100 bg-transparent border border-transparent rounded px-2 py-1 transition-all duration-300 ${
                                                    updatedValues.has(`judgeScore${judgeIndex + 1}-${participant.id}-${event.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30' : ''
                                                  }`}>
                                                    {score}
                                                  </span>
                                                )}
                                              </div>
                                            ))}
                                            <div className="flex flex-col items-center">
                                              <span className="text-xs text-slate-500 dark:text-slate-400 mb-1">P</span>
                                              {(!isPublicView && (editingEventId === event.id || event.status === 'Aktiv')) ? (
                                                <input
                                                  type="number"
                                                  step="0.01"
                                                  min="-2"
                                                  max="2"
                                                  value={participant.penalty}
                                                  onChange={(e) => handlePenaltyChange(event.id, participant.id, parseFloat(e.target.value))}
                                                  className="w-16 px-2 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/20 dark:focus:ring-primary-light/20 focus:border-primary dark:focus:border-primary-light"
                                                  placeholder="0.00"
                                                />
                                              ) : (
                                                <span className={`w-16 text-center text-sm font-semibold bg-transparent border border-transparent rounded px-2 py-1 transition-all duration-300 ${
                                                  updatedValues.has(`penalty-${participant.id}-${event.id}`) ? 'animate-pulse bg-green-100 dark:bg-green-900/30' : ''
                                                } ${
                                                  participant.penalty > 0 ? 'text-green-600 dark:text-green-400' : 
                                                  participant.penalty < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-slate-100'
                                                }`}>
                                                  {participant.penalty > 0 ? '+' : ''}{participant.penalty}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )})}

                                    {/* DNS-Teilnehmer */}
                                    {dnsParticipants.map((participant) => (
                                      <div
                                        key={participant.id}
                                        className="rounded-lg p-4 border border-slate-200 dark:border-slate-700 opacity-50 grayscale bg-white dark:bg-slate-800"
                                      >
                                        {/* Athlete/Team Header */}
                                        <div className="flex items-center gap-4 mb-3">
                                          <div className="flex items-center">
                                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                                              DNS
                                            </span>
                                          </div>
                                          
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                              {participant.name}
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                                <i className="fas fa-times-circle mr-1"></i>
                                                {t("results.didNotStart", "Nicht gestartet")}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                                              <img
                                                src={process.env.PUBLIC_URL + "/images/flags/" + participant.nationCode + ".png"}
                                                alt={participant.nation}
                                                className="w-4 h-4 object-cover rounded-full border border-slate-200 dark:border-slate-600"
                                              />
                                              <span>{participant.nation}</span>
                                              {participant.isTeam && participant.teamSize && (
                                                <span className="text-xs text-slate-400 dark:text-slate-500">
                                                  • {participant.teamSize} Teilnehmer
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          
                                          <div className="text-right">
                                            <div className="text-lg font-bold text-slate-900 dark:text-slate-100">0.00</div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400">Punkte</div>
                                          </div>
                                        </div>
                                      </div>
                                      
                                  ))}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                      ))}
                      
                    </div>
                    
                  )}
                </div>

                </div>
                
              )))}
            </div>
            
          )}
              
      

      </div>  
      </main>

      {/* Toast */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`px-4 py-3 rounded-lg border shadow-lg bg-white dark:bg-slate-800 ${
            toast.type === 'success' ? 'border-green-200 dark:border-green-800' :
            toast.type === 'error' ? 'border-red-200 dark:border-red-800' :
            'border-blue-200 dark:border-blue-800'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                toast.type === 'success' ? 'bg-green-100 dark:bg-green-900/30' :
                toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' :
                'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                <i className={`fas text-xs ${
                  toast.type === 'success' ? 'fa-check text-green-600 dark:text-green-400' :
                  toast.type === 'error' ? 'fa-times text-red-600 dark:text-red-400' :
                  'fa-info text-blue-600 dark:text-blue-400'
                }`}></i>
              </div>
              <span className={`text-sm font-medium ${
                toast.type === 'success' ? 'text-green-900 dark:text-green-300' :
                toast.type === 'error' ? 'text-red-900 dark:text-red-300' :
                'text-blue-900 dark:text-blue-300'
              }`}>{toast.message}</span>
              <button
                onClick={hideToast}
                className="ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsTailwind;
